import type { ReactNode } from 'react';
import { ChevronRight, Loader2, Rocket } from 'lucide-react';

import type { SheetOpportunity } from '../../../hooks/useOpportunitySheet';
import {
  dateLabel,
  money,
  productLabel,
  recurringLabel,
  REVENUE_TYPE_LABEL,
  stageChoices,
} from '../../../lib/crm/opportunitySheet';
import type { CRMPipelineStage } from '../../../types/crm';
import { BTN_SECONDARY } from '../../bureau/buttons';
import { BLOCK_ROW, FIELD } from '../../bureau/encre';
import { Pastille, SheetBlock } from './EncreSheet';

/**
 * « L'affaire »: where the deal stands — its stage, changed here in one gesture (the pipeline's
 * move: the probability follows the stage) — then what it brings and when it should close.
 */
export function DealBlock({
  deal,
  stages,
  busy,
  onStage,
}: {
  deal: SheetOpportunity;
  stages: readonly CRMPipelineStage[];
  busy: boolean;
  onStage: (stage: CRMPipelineStage) => void;
}) {
  const choices = stageChoices(stages, deal.stage_id);
  const oneTime = deal.revenue_type !== 'recurring' ? money(deal.one_time_amount) : null;
  const fields: [string, ReactNode][] = [
    ['Montant', money(deal.amount)],
    ['Récurrent', recurringLabel(deal)],
    ['Ponctuel', oneTime],
    ['Produit', productLabel(deal)],
    ['Revenu', deal.revenue_type !== 'recurring' ? REVENUE_TYPE_LABEL[deal.revenue_type] : null],
    ['Probabilité', deal.probability != null ? `${deal.probability} %` : null],
    // The real closing day is said in the header (« gagnée le … »); an open deal has none.
    ['Clôture visée', dateLabel(deal.target_close_date)],
    ['Raison', deal.status === 'lost' ? deal.reason_lost : null],
  ];
  const known = fields.filter(([, value]) => value);
  const created = dateLabel(deal.created_at);
  const updated = dateLabel(deal.updated_at);
  return (
    <SheetBlock title="L'affaire">
      <dl className="grid grid-cols-[104px_minmax(0,1fr)] items-center gap-x-4 gap-y-1.5 text-sm">
        <dt className="text-desk-muted">
          <label htmlFor={`deal-stage-${deal.id}`}>Étape</label>
        </dt>
        <dd className="m-0 flex min-w-0 items-center gap-2">
          <select
            id={`deal-stage-${deal.id}`}
            value={deal.stage_id ?? ''}
            disabled={busy || choices.length === 0}
            onChange={(e) => {
              const stage = choices.find((s) => s.id === e.target.value);
              if (stage) onStage(stage);
            }}
            className={`${FIELD} max-w-[15rem]`}
          >
            {!deal.stage_id && <option value="">Aucune étape</option>}
            {choices.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
          {busy && <Loader2 size={15} className="animate-spin text-desk-muted" aria-label="Enregistrement…" />}
        </dd>
        {known.map(([label, value]) => (
          <div key={label} className="contents">
            <dt className="text-desk-muted">{label}</dt>
            <dd className="m-0 min-w-0 break-words text-desk-strong">{value}</dd>
          </div>
        ))}
      </dl>
      {known.length === 0 && <p className="mt-2 text-sm text-desk-muted">Ni montant ni date : « Modifier » permet de les poser.</p>}
      {created && (
        <p className="mt-3 text-xs text-desk-muted">
          Créée le {created}
          {updated && updated !== created && ` · modifiée le ${updated}`}
        </p>
      )}
    </SheetBlock>
  );
}

/**
 * « Onboarding »: the client's onboarding lead once the deal is won — opened in its sheet —, or the
 * gesture that opens it when the deal is won without one.
 */
export function LeadBlock({
  deal,
  busy,
  onOpenLead,
  onCreateLead,
}: {
  deal: SheetOpportunity;
  busy: boolean;
  onOpenLead: (id: string) => void;
  onCreateLead: () => void;
}) {
  const lead = deal.lead;
  if (!lead && deal.status !== 'won') return null;
  return (
    <SheetBlock title="Onboarding">
      {lead ? (
        <button
          type="button"
          onClick={() => onOpenLead(lead.id)}
          className={BLOCK_ROW}
        >
          <Pastille icon={Rocket} />
          <span className="min-w-0 flex-1 truncate text-sm font-medium text-desk-ink">{lead.company_name?.trim() || 'Lead d’onboarding'}</span>
          <ChevronRight size={16} className="shrink-0 text-desk-faint" aria-hidden="true" />
        </button>
      ) : (
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="text-sm text-desk-soft">Affaire gagnée, sans lead d’onboarding.</p>
          <button type="button" onClick={onCreateLead} disabled={busy} className={BTN_SECONDARY}>
            {busy && <Loader2 size={15} className="animate-spin" aria-hidden="true" />}
            Ouvrir le lead
          </button>
        </div>
      )}
    </SheetBlock>
  );
}
